import React, { useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useHistory } from 'react-router-dom';
import Button from '../Button';
import { SearchIcon } from '../Icons';
import { updateVisualMatches } from '../../../graphql/likiees/mutations';
import { useCredits } from '../../../hooks/useCredits';
import './VisualSearchButton.scss';

const VisualSearchButton = ({
  likieeId,
  likiee = null, // Optional prop to receive full likiee object - maintains backward compatibility
  onSuccess,
  disabled = false,
  className = '',
  isVisualMatchesInProgress,
  isEnhancingInProgress, // Keep for backward compatibility
  showAsStartNew = false,
  showCreditCost = true, // Default to true to show credit cost
}) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const history = useHistory();
  const { checkCredits } = useCredits();
  const [updating, setUpdating] = useState(false);

  // Use isVisualMatchesInProgress if provided, otherwise fall back to isEnhancingInProgress
  const inProgress = isVisualMatchesInProgress !== undefined ? isVisualMatchesInProgress : isEnhancingInProgress;

  // Get credit cost from configuration
  const creditCost = 5; // Visual search costs 1 credit as defined in creditOperations.js

  // Check for fashion-specific logic - only when aiEnhancement is missing
  const isFashionWithoutEnhancement = likiee && !likiee.aiEnhancement && likiee.aiSummary?.imageType === 'fashion';

  // Determine button text based on visual matches state and fashion logic
  const getButtonText = () => {
    if (updating) return t('searching...');

    // If visual matches are in progress, show "Find Similar"
    if (inProgress === true && !showAsStartNew) {
      return t('Find Similar');
    }

    // For start new search scenarios
    if (showAsStartNew || inProgress !== true) {
      // Only show fashion-specific text if aiEnhancement is missing AND imageType is fashion
      if (isFashionWithoutEnhancement) {
        return t('Try AI Look finder');
      }
      // Default text for start new search
      return t('Start New Search');
    }

    // Fallback
    return t('Find Similar');
  };

  const handleVisualSearch = async () => {
    if (disabled || updating) return;

    // Check if user has sufficient credits
    const hasEnoughCredits = checkCredits('VISUAL_SEARCH', creditCost);

    if (!hasEnoughCredits) {
      // Redirect to subscription page if insufficient credits
      history.push('/profile/subscription');
      return;
    }

    setUpdating(true);
    try {
      // Call the updateVisualMatches mutation directly
      const result = await API.graphql(
        graphqlOperation(updateVisualMatches, {
          likieeId: likieeId,
        }),
      );

      // Check if the mutation was successful
      if (result.data?.updateVisualMatches?.error) {
        throw new Error(result.data.updateVisualMatches.error);
      }

      alert.success(t('visual_search_started'));
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      alert.error(t('visual_search_failed'));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Button
      className={`visual-search-button ${className} ${
        showAsStartNew || inProgress !== true ? 'start-new' : 'enhanced-state'
      } ${updating ? 'loading' : ''} mobile-compact`}
      onClick={handleVisualSearch}
      disabled={disabled || updating}
      loading={updating}
      variant='outline'
      size='md'
    >
      <span className='button-content'>
        <SearchIcon className='search-icon' />
        <span className='button-text'>{getButtonText()}</span>
        {showCreditCost && (
          <span className='credit-cost'>
            {creditCost} credit{creditCost !== 1 ? 's' : ''}
          </span>
        )}
      </span>
    </Button>
  );
};
export default VisualSearchButton;
